import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useI18n } from '../../i18n';
import { useAuth } from '../../context/AuthContext';

export default function AdminUsers() {
  const { t, locale } = useI18n();
  const { user: currentUser, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { personnel_number: '', password: '', first_name: '', last_name: '', email: '', role_id: '', supervisor_id: '', weekly_hours: 40, vacation_days_per_year: 30, entry_date: '' };
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [uRes, rRes] = await Promise.all([api.get('/users/'), api.get('/roles/')]);
    setUsers(uRes.data);
    setRoles(rRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const isGlobalManager = hasPermission('admin') || hasPermission('manage_users');

  // Nachfahren des bearbeiteten Benutzers dürfen nicht dessen Vorgesetzte werden (Zyklus)
  const descendantIds = (rootId) => {
    const found = new Set();
    const open = [rootId];
    while (open.length) {
      const current = open.pop();
      users.forEach((x) => {
        if (x.supervisor_id === current && !found.has(x.id)) {
          found.add(x.id);
          open.push(x.id);
        }
      });
    }
    return found;
  };

  // Nicht-Admins: nur die eigene Person und eigene Untergebene (die Liste enthält nur diese)
  const supervisorOptions = (() => {
    const blocked = editing ? descendantIds(editing.id) : new Set();
    const candidates = isGlobalManager || !currentUser || users.some((x) => x.id === currentUser.id)
      ? users
      : [currentUser, ...users];
    return candidates.filter((x) => !editing || (x.id !== editing.id && !blocked.has(x.id)));
  })();

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ personnel_number: u.personnel_number, password: '', first_name: u.first_name, last_name: u.last_name, email: u.email || '', role_id: u.role?.id || '', supervisor_id: u.supervisor_id || '', weekly_hours: u.weekly_hours ?? 40, vacation_days_per_year: u.vacation_days_per_year ?? 30, entry_date: u.entry_date || '' });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = { ...form, role_id: form.role_id ? +form.role_id : null, supervisor_id: form.supervisor_id ? +form.supervisor_id : null, weekly_hours: +form.weekly_hours, vacation_days_per_year: +form.vacation_days_per_year, entry_date: form.entry_date || null };
      if (!payload.password) delete payload.password;
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post('/users/', payload);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('admin.common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.users.confirmDelete'))) return;
    await api.delete(`/users/${id}`);
    await load();
  };

  const handleToggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { is_active: !u.is_active });
    await load();
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0"><i className="bi bi-people me-2" />{t('admin.users.title')}</h4>
        <button className="btn btn-primary" onClick={openCreate}><i className="bi bi-person-plus me-2" />{t('admin.users.new')}</button>
      </div>

      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h6>{editing ? t('admin.users.edit') : t('admin.users.new')}</h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">{t('admin.users.personnelNumber')}</label>
                  <input className="form-control" required value={form.personnel_number}
                    onChange={(e) => setForm({ ...form, personnel_number: e.target.value })}
                    disabled={!!editing} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('admin.users.password')} {editing ? t('admin.users.passwordKeep') : '*'}</label>
                  <input className="form-control" type="password" required={!editing} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('admin.users.firstName')}</label>
                  <input className="form-control" required value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('admin.users.lastName')}</label>
                  <input className="form-control" required value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('admin.users.email')}</label>
                  <input className="form-control" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('admin.users.role')}</label>
                  <select className="form-select" value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })}>
                    <option value="">{t('admin.users.noRole')}</option>
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('admin.users.supervisor')}</label>
                  <select className="form-select" value={form.supervisor_id} onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })}>
                    <option value="">{t('admin.users.noSupervisor')}</option>
                    {supervisorOptions.map((x) => (
                      <option key={x.id} value={x.id}>{x.first_name} {x.last_name} ({x.personnel_number})</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('admin.users.weeklyHours')}</label>
                  <input className="form-control" type="number" required min="0.5" max="80" step="0.5" value={form.weekly_hours}
                    onChange={(e) => setForm({ ...form, weekly_hours: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('admin.users.vacationDays')}</label>
                  <input className="form-control" type="number" required min="0" step="0.5" value={form.vacation_days_per_year}
                    onChange={(e) => setForm({ ...form, vacation_days_per_year: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('admin.users.entryDate')}</label>
                  <input className="form-control" type="date" value={form.entry_date}
                    onChange={(e) => setForm({ ...form, entry_date: e.target.value })} />
                </div>
              </div>
              {error && <div className="alert alert-danger mt-2 py-2">{error}</div>}
              <div className="mt-3 d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting && <span className="spinner-border spinner-border-sm me-2" />}{t('admin.common.save')}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>{t('admin.common.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <div className="text-center"><div className="spinner-border" /></div> : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>{t('admin.users.col.nr')}</th><th>{t('admin.users.col.name')}</th><th>{t('admin.users.col.email')}</th><th>{t('admin.users.col.role')}</th><th>{t('admin.users.col.supervisor')}</th><th>{t('admin.users.col.status')}</th><th>{t('admin.users.col.created')}</th><th>{t('admin.users.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={!u.is_active ? 'table-secondary' : ''}>
                    <td><code>{u.personnel_number}</code></td>
                    <td>{u.first_name} {u.last_name}</td>
                    <td><small>{u.email || '–'}</small></td>
                    <td>{u.role ? <span className="badge bg-primary">{u.role.name}</span> : <span className="badge bg-secondary">–</span>}</td>
                    <td><small>{(() => { const s = users.find((x) => x.id === u.supervisor_id); return s ? `${s.first_name} ${s.last_name}` : '–'; })()}</small></td>
                    <td>
                      <span className={`badge ${u.is_active ? 'bg-success' : 'bg-danger'}`}>
                        {u.is_active ? t('admin.users.active') : t('admin.users.inactive')}
                      </span>
                    </td>
                    <td><small>{u.created_at ? new Date(u.created_at).toLocaleDateString(locale) : '–'}</small></td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <Link className="btn btn-outline-secondary" to={`/admin/users/${u.id}`} title={t('admin.userDetail.open')}><i className="bi bi-person-lines-fill" /></Link>
                        <button className="btn btn-outline-primary" onClick={() => openEdit(u)} title={t('admin.common.edit')}><i className="bi bi-pencil" /></button>
                        <button className="btn btn-outline-warning" onClick={() => handleToggleActive(u)} title={u.is_active ? t('admin.users.deactivate') : t('admin.users.activate')}>
                          <i className={`bi ${u.is_active ? 'bi-person-slash' : 'bi-person-check'}`} />
                        </button>
                        <button className="btn btn-outline-danger" onClick={() => handleDelete(u.id)} title={t('admin.common.delete')}><i className="bi bi-trash" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

